import { AfterViewInit, afterNextRender, ChangeDetectionStrategy, Component, computed, effect, ElementRef, EventEmitter, HostListener, inject, Injector, input, OnDestroy, OnInit, Output, viewChild } from '@angular/core';
import { eventFromOverlay } from 'src/app/infrastructure/functions/event-from-overlay.function';

import { UntypedFormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';

import { MatCheckbox } from '@angular/material/checkbox';

import { FilterService } from '../filter.service';
import { SortDirection } from 'src/app/infrastructure/pipes/filter.pipe';
import { InputControlComponent } from '../../controls/container/input-control/input-control.component';
import { appConfig } from 'src/config';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { ButtonNeutralComponent } from '../../controls/container/button-neutral/button-neutral.component';
import { TabListService } from '../../central-page/services/tab-list.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { humanizeFieldName } from 'src/app/infrastructure/functions/humanize-field-name.function';
import { IconComponent } from '../../icons/container/icon.component';

const showTime = appConfig.centralPage_filterShow_time;
const hideTime = appConfig.centralPage_filterHide_time;
const showContentTime = appConfig.centralPage_filterContentShow_time;
const hideContentTime = appConfig.centralPage_filterContentHide_time;

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        InputControlComponent,
        MatButtonToggleGroup,
        ButtonNeutralComponent,
        MatCheckbox,
        IconComponent,
    ],
    styleUrls: ['./filter.component.scss'],
    animations: [
        [
            trigger('filterShow', [
                transition(':enter', [
                    style({opacity: '0', height: '0'}),
                    animate(`${showTime} ease-in-out`, style({height: '*', opacity: '1'}))
                ]),
                transition(':leave', [
                    style({opacity: '1', height: '*'}),
                    animate(`${hideTime} ease-in-out`, style({height: '0', opacity: '0'}))
                ])
            ]),
            trigger('slideFilterContent', [
                transition(':enter', [
                    style({opacity: '0'}),
                    animate(`${showContentTime} ease-in-out`, style({opacity: '1'}))
                ]),
                transition(':leave', [
                    style({opacity: '1'}),
                    animate(`${hideContentTime} ease-in-out`, style({opacity: '0'}))
                ])
            ]),
            trigger('settingsPanel', [
                transition(':enter', [
                    style({opacity: '0', height: '0'}),
                    animate(`${showContentTime} ease-in-out`, style({height: '*', opacity: '1'}))
                ]),
                transition(':leave', [
                    style({opacity: '1', height: '*'}),
                    animate(`${hideContentTime} ease-in-out`, style({height: '0', opacity: '0'}))
                ])
            ]),
        ],
    ]
})
export class FilterComponent 
    implements AfterViewInit, OnInit, OnDestroy
{
    readonly filterService = injectRequired(FilterService);
    private readonly _tabListService = injectRequired(TabListService);

    private __inputControlComponent$$ = viewChild<InputControlComponent>('inputControlComponent');
    private __settingsPanel$$ = viewChild<ElementRef<HTMLElement>>('settingsPanel');
    private __ellipsisBtn$$ = viewChild<ElementRef<HTMLElement>>('ellipsisBtn');
    private __settingsGrid$$ = viewChild<ElementRef<HTMLElement>>('settingsGrid');

    private readonly __injector = inject(Injector);

    // FLIP-animatie voor het herschikken van de sorteervolgorde (zelfde techniek + duration-token
    // als de foto-tablist, zie __readDragDuration in image-tablist.component.ts): posities vóór de
    // toggle vastleggen, en na de eerstvolgende render — wanneer orderedFields$$ de rij al verplaatst
    // heeft — het verschil wegannimeren zodat de rij zichtbaar naar zijn nieuwe plek schuift i.p.v.
    // abrupt te verspringen. Zuiver verticaal: de kolom (label/checkbox/sorteer-knop) van een veld
    // verandert nooit, alleen zijn rij-positie.
    private readonly __flipPositions = new Map<HTMLElement, number>();
    private readonly __flipAnimations = new Map<HTMLElement, Animation>();

    input_filterShow$$ = input(null, {alias: 'show'});

    filterShow$$ = this.filterService.filterShow$$;
    filterString$$ = this.filterService.filterString$$;
    filterMode$$ = this.filterService.filterMode$$;

    searchFields$$ = this.filterService.searchFields$$;
    searchFieldsActive$$ = this.filterService.searchFieldsActive$$;
    sortFields$$ = this.filterService.sortFields$$;
    settingsOpen$$ = this.filterService.settingsOpen$$;
    fieldLabel = humanizeFieldName;
    // Zoeksyntax van filterSearch (filter.pipe.ts), als native tooltip op het invoerveld.
    readonly filterSyntaxHint = 'woord: bevat woord\n"twee woorden": bevat de frase\n=woord: hele waarde is exact woord (bv. =-)';

    // Weergavevolgorde in het instellingenpaneel: actieve sorteervelden vóóraan (in ketenvolgorde =
    // prioriteit), overige velden erna — maar bewust NIET hier herberekend uit de actuele sorteerketen
    // (dat liet de lijst bij een reset terugspringen naar de statische veld-declaratievolgorde i.p.v.
    // stabiel te blijven staan). Komt nu uit TabDataService.fieldOrder$$, dat alleen bijwerkt bij het
    // NIEUW activeren van een veld.
    orderedFields$$ = this.filterService.fieldOrder$$;
    filterMode_tooltip$$ = computed(() =>
        this.filterMode$$() === 'and' ? 'AND - klik voor OR' : 'OR - klik voor AND'
    );
    filterMode_icon$$ = computed(() =>
        this.filterMode$$() === 'and' ? '[..]' : '.[].'
    );

    trigger$ = this._tabListService.tabIndex$;

    filterControl = new UntypedFormControl('');

    @Output('onChange') filterChangeEmit = new EventEmitter();

    @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
        const _filterShow = this.filterService.filterShow$$();
        const _filterString = this.filterString$$();
        const _filterActive = this.filterService.filterActive;

        if (event.key !== 'Escape' || !_filterActive || !_filterShow || eventFromOverlay(event)) return;

        // Gefaseerd sluiten, één stap per Esc-druk: 1) instellingen-paneel, 2) filtertekst wissen,
        // 3) filterbalk verbergen. Zolang de filter iets te sluiten heeft, consumeren we de Esc
        // (stopPropagation) zodat andere Esc-acties pas aan de beurt zijn als de filterbalk dicht is.
        if (this.settingsOpen$$()) {
            this.filterService.settingsOpen_close();
        } else if (_filterString) {
            this.filterService.FilterString = '';
        } else {
            this.filterService.FilterShow = false;
        }
        event.stopPropagation();
    }

    @HostListener('document:click', ['$event']) handleDocumentClick(event: MouseEvent) {
        if (!this.settingsOpen$$()) return;

        const _target = event.target as Node;
        const _panel = this.__settingsPanel$$()?.nativeElement;
        const _ellipsis = this.__ellipsisBtn$$()?.nativeElement;

        // Klik bínnen het paneel of op de ellipsis-knop (die togglet zelf) laat het paneel met rust.
        if (_panel?.contains(_target) || _ellipsis?.contains(_target)) return;

        // Een matTooltip rendert in de globale CDK-overlay, dus buiten het paneel in de DOM-boom —
        // zonder deze uitzondering sloot een (per ongeluk) klik op de tooltip-tekst zelf van bv. de
        // sorteer-knop het instellingenpaneel meteen af.
        if (_target instanceof Element && _target.closest('.mat-mdc-tooltip-panel')) return;

        this.filterService.settingsOpen_close();
    }

    constructor() {
        effect(() => {
            const _filterString = this.filterString$$() ?? '';
            const _filterControlValue = this.filterControl.value;
            
            if (_filterControlValue != _filterString) {
                this.filterControl.setValue(_filterString);
            }
        });
    }

    ngOnInit() {        
        this.filterService.FilterShow = !!this.filterString$$();
    }

    emit_filterShowDone(): void {
        const _filterShow = this.filterService.filterShow$$();

        if (_filterShow) {
            setTimeout(() => {
                const _inputControl = this.__inputControlComponent$$();

                _inputControl?.inputField?.nativeElement.focus();
            }, 0);
        }

    }

    emit_focusIn(): void {
        this.filterService.inputDetection_set(true);
    }

    emit_focusOut(): void {
        this.filterService.inputDetection_set(false);
    }

    emit_filterClear(): void {
        this.filterService.FilterString = '';
        this.filterChange_emit();
        this.__inputControlComponent$$()?.inputField?.nativeElement.focus();
    }

    emit_filterMode_toggle(): void {
        this.filterService.filterMode_toggle();
        this.filterChange_emit();
    }

    emit_searchField_toggle(field: string): void {
        this.filterService.searchField_toggle(field);
        this.filterChange_emit();
    }

    emit_sort_toggle(field: string): void {
        const __grid = this.__settingsGrid$$()?.nativeElement;
        if (__grid) this.__flipCapture(__grid);

        this.filterService.sortField_toggle(field);
        this.filterChange_emit();

        if (__grid) {
            afterNextRender(() => this.__flipAnimate(__grid), { injector: this.__injector });
        }
    }

    sortDirection(field: string): SortDirection | null {
        return this.sortFields$$().find(s => s.field === field)?.direction ?? null;
    }

    emit_settings_toggle(): void {
        this.filterService.settingsOpen_toggle();
    }

    fieldActive(field: string): boolean {
        return this.searchFieldsActive$$().includes(field);
    }

    filterChange_emit(): void {
        this.filterChangeEmit.emit();
    }

    private __flipItems(grid: HTMLElement): HTMLElement[] {
        return Array.from(grid.querySelectorAll<HTMLElement>('.settings-flip-item'));
    }

    private __flipCapture(grid: HTMLElement): void {
        this.__flipPositions.clear();
        for (const __el of this.__flipItems(grid)) {
            this.__flipPositions.set(__el, __el.offsetTop);
        }
    }

    // Duur uit de theming-token (--layout-motion-duration-drag), zelfde patroon als __readDragDuration
    // in image-tablist.component.ts.
    private __flipDurationMs(grid: HTMLElement): number {
        const __raw = getComputedStyle(grid).getPropertyValue('--layout-motion-duration-drag').trim();
        const __n = parseFloat(__raw);
        if (!Number.isFinite(__n)) return 250;
        return __raw.endsWith('ms') || !__raw.endsWith('s') ? __n : __n * 1000;
    }

    private __flipAnimate(grid: HTMLElement): void {
        const __duration = this.__flipDurationMs(grid);
        for (const __el of this.__flipItems(grid)) {
            const __last = __el.offsetTop;
            const __first = this.__flipPositions.get(__el);
            this.__flipPositions.set(__el, __last);
            if (__first === undefined) continue;

            const __dy = __first - __last;
            if (__dy === 0) continue;

            this.__flipAnimations.get(__el)?.cancel();
            const __anim = __el.animate(
                [{ transform: `translateY(${__dy}px)` }, { transform: 'translateY(0)' }],
                { duration: __duration, easing: 'cubic-bezier(0, 0, 0.2, 1)' },
            );
            this.__flipAnimations.set(__el, __anim);
        }
    }

    ngAfterViewInit(): void {
        this.filterControl.valueChanges
            .pipe(
                debounceTime(150),
                distinctUntilChanged(),
            )
            .subscribe(filterString => {
                const _inputDetection = this.filterService.InputDetection;

                if (_inputDetection) {
                    this.filterService.FilterString = filterString;
                    this.filterChange_emit();
                }
                this.filterService.inputDetection_set(true);   // When changing [tabData] in TabDataService [inputDetection] = false
            });
    }

    ngOnDestroy(): void {
        this.filterChangeEmit?.unsubscribe();
    }
}

